/**
 * UX copy and helpers for the server-enforced export-element quota.
 *
 * The authoritative counter is `License.RemainingExportElements` (null =
 * unlimited) and it is measured in exported Revit elements, not in export
 * runs; the client only mirrors what the server reported — see
 * `useExportQuotaStore`. All user-facing text is English by policy.
 */
export { PCF_EXPORT_QUOTA_EXHAUSTED_CODE } from '../../../shared/api/grpc/pcfRunApi';

/** Gentle entitlement guidance shown when the allowance reaches zero. */
export function getQuotaExhaustedMessage(isDemo: boolean): string {
  return isDemo
    ? "Your demo's export element limit is used up. Every export subtracts the number of exported elements, and no elements are left. To continue exporting, contact your administrator to raise the limit or choose a paid license."
    : "Your license's export element limit is used up. Every export subtracts the number of exported elements, and no elements are left. To continue exporting, contact your administrator to raise the limit.";
}

/** Guidance for a rejected request that is larger than a positive balance. */
export function getQuotaInsufficientMessage(isDemo: boolean, remaining: number): string {
  return `${formatRemainingExportElementsStatus(isDemo, remaining)}. This export needs more elements than are currently available. Export a smaller selection and retry.`;
}

/** Selects recovery guidance from the authoritative quota response. */
export function getQuotaFailureMessage(isDemo: boolean, remaining: number | null | undefined): string {
  return typeof remaining === 'number' && remaining > 0
    ? getQuotaInsufficientMessage(isDemo, remaining)
    : getQuotaExhaustedMessage(isDemo);
}

/** Compact counter, e.g. `Demo · 820 export elements remaining`. Always shows
 *  the actual server value — the limit is editable per license. */
export function formatRemainingExportElementsStatus(isDemo: boolean, remaining: number): string {
  const noun = `export element${remaining === 1 ? '' : 's'}`;
  return isDemo
    ? `Demo · ${remaining} ${noun} remaining`
    : `${remaining} ${noun} remaining`;
}

/** Unobtrusive explanatory text rendered next to the counter. */
export function getRemainingExportElementsHint(isDemo: boolean): string {
  return isDemo
    ? 'Every export subtracts the number of exported elements; demo licenses start with 1000 elements and your administrator can change the limit.'
    : 'Every export subtracts the number of exported elements; your administrator sets the limit.';
}
